
// Pure geometric core for post-digitizing line-feature editing ("Vektor-
// operationer" - see tools/vectorEditingMenu.js for the interactive wiring).
// No DOM/THREE-scene/camera dependency: every function here takes and
// returns plain {x,y,z} points, so it's Level 1 (no-browser) unit testable -
// see vectorEditing.test.js, and CLAUDE.md's Test-hierarki.
//
// Every operation that can move a vertex returns {points, maxDeviation} (or
// similar) - `points` is the FULL new vertex array (same convention
// throughout, only touching the requested [startIndex..endIndex] range, if
// given), and `maxDeviation` is the largest distance any single vertex
// actually moved, in the same units as the input coordinates. Callers
// compare that against "Maksimal pilhøjde" (see settings/SettingsSchema.js's
// "vectorEditing" group) BEFORE committing - this module never enforces
// that limit itself by silently deforming a result; see each function's own
// doc comment for how it treats the limit (straighten refuses outright,
// simplify/orthogonalize/align respect it by construction).
//
//   Vektoroperationer (geometry core)
//           |
//           +-- straightenLine2D()      - section 3
//           +-- straightenLine3D()      - section 4
//           +-- simplifyPolyline()      - section 5 (Douglas-Peucker)
//           +-- orthogonalizePolyline() - section 7
//           +-- computeFixedAngleVertex() - section 8
//           +-- alignSegmentParallel()/alignSegmentPerpendicular() - section 9
//           +-- insertPointOnSegment()  - section 10
//           +-- reverseDirection()      - section 11
//           +-- movePoint()             - section 12
//           +-- distributePointsEvenly() - section 13
//           +-- movePointInZ()          - section 14
//           +-- extendEndpointByDistance() - section 15
//           +-- setAbsoluteZ()          - section 16

function clonePoint(p){
	return {x: p.x, y: p.y, z: p.z};
}

function dist2D(a, b){
	return Math.hypot(a.x - b.x, a.y - b.y);
}

function dist3D(a, b){
	return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

// ── section 3: Ret ud i 2D ──────────────────────────────────────────────
//
// Anchors are points[startIndex] and points[endIndex] (default: the whole
// vector). Every strictly-interior vertex is replaced by its orthogonal
// projection onto the infinite XY line through the two anchors (NOT clamped
// to the segment between them - the task's own definition, "projicér...
// ortogonalt på denne linje", is the line, not the segment). Z is left
// completely untouched on every vertex, including the interior ones -
// straightenLine2D never reads or writes Z at all beyond copying it through
// unchanged.
export function straightenLine2D(points, startIndex = 0, endIndex = points.length - 1){
	const P0 = points[startIndex];
	const Pn = points[endIndex];
	const dx = Pn.x - P0.x;
	const dy = Pn.y - P0.y;
	const lenSq = dx * dx + dy * dy;

	const result = points.map(clonePoint);
	let maxDeviation = 0;

	for(let i = startIndex + 1; i < endIndex; i++){
		const p = points[i];
		// degenerate anchors (P0≈Pn in XY): no direction to project onto -
		// falls back to collapsing onto P0 itself, the least-arbitrary
		// choice available or the given (degenerate) input
		const t = lenSq > 1e-12 ? ((p.x - P0.x) * dx + (p.y - P0.y) * dy) / lenSq : 0;
		const projX = P0.x + t * dx;
		const projY = P0.y + t * dy;

		maxDeviation = Math.max(maxDeviation, Math.hypot(p.x - projX, p.y - projY));
		result[i] = {x: projX, y: projY, z: p.z};
	}

	return {points: result, maxDeviation};
}

// ── section 4: Ret ud i 3D ──────────────────────────────────────────────
//
// UNLIKE straightenLine2D, there are no anchors here: NO vertex in
// [startIndex..endIndex] is held fixed, including the two boundary vertices
// of that range (task requirement - "der er ikke noget vertex der skal
// fastholdes, hverken endevertex eller mellemvertex"). Every vertex in range
// is instead treated as equal evidence for what the straight line THROUGH
// them all should be, and every one of them - endpoints included - is then
// moved onto that line:
//
//   1. the target line's origin is the CENTROID of every vertex in range
//      (not one arbitrarily privileged endpoint);
//   2. its direction is the PRINCIPAL AXIS of that vertex set - the 3D
//      direction of greatest variance, found by power iteration on the 3x3
//      covariance matrix (same technique pipeTracking.js's own
//      predictAhead() uses for its robust line fit - a few iterations
//      converge quickly and need no external linear-algebra dependency);
//   3. every vertex, including startIndex and endIndex themselves, is
//      replaced by its orthogonal projection onto that line.
//
// The result is still a perfectly straight run (every returned vertex is
// exactly collinear) - "Ret ud" - but the line itself is now a genuine
// best-fit through the WHOLE selected stretch rather than a chord between
// two points that happened to be its ends, and nothing in the range is
// exempt from moving onto it. For a 2-point range this reduces to exactly
// the old anchor-chord behaviour (the best-fit line through 2 points IS the
// line through them, so both project onto themselves - maxDeviation 0);
// for an all-coincident range there is no meaningful direction, so the
// vertices are left where they already are (again maxDeviation 0).
//
// Deliberately a separate, independent implementation from straightenLine2D
// (not a shared "project in N dimensions" helper toggled by a flag) - see
// that function's own doc comment: the two operations are conceptually
// different (straightenLine2D still keeps its own two anchors and never
// touches Z at all), and reading either on its own should show exactly what
// it does, without tracing through a shared abstraction.
const STRAIGHTEN_3D_POWER_ITERATIONS = 4;

export function straightenLine3D(points, startIndex = 0, endIndex = points.length - 1){
	const result = points.map(clonePoint);
	const n = endIndex - startIndex + 1;
	if(n < 2) return {points: result, maxDeviation: 0};

	let cx = 0, cy = 0, cz = 0;
	for(let i = startIndex; i <= endIndex; i++){
		cx += points[i].x; cy += points[i].y; cz += points[i].z;
	}
	cx /= n; cy /= n; cz /= n;

	// initial direction guess: the range's own current end-to-end chord,
	// falling back to an arbitrary axis only if every vertex coincides
	const P0 = points[startIndex], Pn = points[endIndex];
	let dx = Pn.x - P0.x, dy = Pn.y - P0.y, dz = Pn.z - P0.z;
	let dirLen = Math.hypot(dx, dy, dz);
	if(dirLen < 1e-12){ dx = 1; dy = 0; dz = 0; dirLen = 1; }
	dx /= dirLen; dy /= dirLen; dz /= dirLen;

	for(let iter = 0; iter < STRAIGHTEN_3D_POWER_ITERATIONS; iter++){
		let covX = 0, covY = 0, covZ = 0;
		for(let i = startIndex; i <= endIndex; i++){
			const vx = points[i].x - cx, vy = points[i].y - cy, vz = points[i].z - cz;
			const proj = vx * dx + vy * dy + vz * dz;
			covX += vx * proj; covY += vy * proj; covZ += vz * proj;
		}
		const covLen = Math.hypot(covX, covY, covZ);
		if(covLen > 1e-12){ dx = covX / covLen; dy = covY / covLen; dz = covZ / covLen; }
	}

	let maxDeviation = 0;
	for(let i = startIndex; i <= endIndex; i++){
		const p = points[i];
		const vx = p.x - cx, vy = p.y - cy, vz = p.z - cz;
		const t = vx * dx + vy * dy + vz * dz;
		const projX = cx + t * dx, projY = cy + t * dy, projZ = cz + t * dz;

		maxDeviation = Math.max(maxDeviation, Math.hypot(p.x - projX, p.y - projY, p.z - projZ));
		result[i] = {x: projX, y: projY, z: projZ};
	}

	return {points: result, maxDeviation};
}

// ── section 5: Forenkle (Douglas-Peucker / Ramer-Douglas-Peucker) ───────
//
// Standard recursive DP simplification: within [startIndex..endIndex], the
// point with the largest perpendicular distance from the chord connecting
// the range's current endpoints is kept (and the range split there and
// recursed) if that distance exceeds `tolerance`; otherwise every point
// strictly between the endpoints is dropped. startIndex/endIndex themselves
// are always kept (task requirement: first/last vertex of the *edited
// range* survive - for a whole-vector simplify that's the vector's own
// first/last vertex).
//
// Distance metric: perpendicular 3D distance from the point to the
// *infinite line* through the range's current two endpoints (the classic
// Douglas-Peucker metric - not distance-to-segment), using the true 3D
// cross-product magnitude rather than an XY-only cross product. One clear 3D
// semantics is used everywhere (no separate 2D/2.5D variant) so a vertex
// representing an important Z-knæk (e.g. a pipe's vertical bend) is never
// wrongly discarded just because the line is nearly straight in plan. This
// is a direct generalisation of the previous 2D formula and is numerically
// IDENTICAL to it whenever Z is constant along the range (a 3D cross
// product with a zero Z-component on every input reduces exactly to the 2D
// cross product), so every pre-existing all-Z=0 simplify result is
// unaffected by this change.
function cross3DMagnitude(ax, ay, az, bx, by, bz){
	const cx = ay * bz - az * by;
	const cy = az * bx - ax * bz;
	const cz = ax * by - ay * bx;
	return Math.hypot(cx, cy, cz);
}

export function simplifyPolyline(points, tolerance, startIndex = 0, endIndex = points.length - 1){
	const keep = new Array(points.length).fill(false);
	keep[startIndex] = true;
	keep[endIndex] = true;

	let maxDeviation = 0;

	const recurse = (lo, hi) => {
		if(hi - lo < 2) return;

		const A = points[lo];
		const B = points[hi];
		const dx = B.x - A.x;
		const dy = B.y - A.y;
		const dz = B.z - A.z;
		const lineLen = Math.hypot(dx, dy, dz);

		let farIndex = -1;
		let farDist = 0;

		for(let i = lo + 1; i < hi; i++){
			const p = points[i];
			const d = lineLen > 1e-12
				? cross3DMagnitude(p.x - A.x, p.y - A.y, p.z - A.z, dx, dy, dz) / lineLen
				: dist3D(p, A);
			if(d > farDist){ farDist = d; farIndex = i; }
		}

		if(farIndex === -1) return;

		if(farDist > tolerance){
			// farIndex survives (kept) - farDist is NOT a removed point's
			// error, so it must not feed into the maxDeviation diagnostic;
			// only the recursive calls' own eventual "everything here is
			// being dropped" distances (the else-branch below) represent an
			// actual committed deviation
			keep[farIndex] = true;
			recurse(lo, farIndex);
			recurse(farIndex, hi);
		}else{
			// every point in (lo,hi) is within tolerance of the chord and
			// stays dropped - farDist is the true worst-case deviation this
			// particular removal incurs
			maxDeviation = Math.max(maxDeviation, farDist);
		}
	};

	recurse(startIndex, endIndex);

	const result = [];
	for(let i = 0; i < points.length; i++){
		if(i < startIndex || i > endIndex || keep[i]) result.push(clonePoint(points[i]));
	}

	const removedCount = (endIndex - startIndex + 1) - keep.slice(startIndex, endIndex + 1).filter(Boolean).length;

	return {points: result, maxDeviation, removedCount};
}

// ── section 7: Ortogonaliser ─────────────────────────────────────────────
//
// Iterative, deterministic relaxation - not a closed-form global solve
// (general simultaneous multi-vertex angle-constraint satisfaction has no
// single closed form once more than one vertex is involved). Each iteration
// works from a *snapshot* of the previous iteration's positions (not
// positions already updated earlier in the same pass), so neighbouring
// vertices are corrected symmetrically/independently within a pass rather
// than the order they happen to be visited in biasing the result - task
// requirement "behandle nabosegmenter symmetrisk".
//
// Per flagged vertex, the target position for *this iteration's step* is
// the exact, closed-form minimum-displacement point that makes the angle at
// that vertex exactly 90°/180° while treating its two immediate neighbours
// (from the snapshot) as fixed anchors - see projectPointToFixedAngleArc()
// - then only a damped fraction of the way there is actually taken (STEP_
// FRACTION), and the step is clamped so the vertex's *cumulative* movement
// from its ORIGINAL position never exceeds `maxSagitta`. Repeating this
// lets multiple interacting vertices (e.g. two adjacent near-right-angle
// bends) settle into a mutually consistent configuration instead of each
// jumping straight to a solution that assumed its neighbour wouldn't also
// move.
//
// MAX_ITERATIONS/CONVERGENCE_TOLERANCE/STEP_FRACTION are internal algorithm
// parameters (task requirement: never exposed as user settings) - only
// `angleToleranceDeg` (which vertices are eligible at all) and `maxSagitta`
// (the hard displacement budget) are caller-supplied / user-facing.
const ORTHOGONALIZE_MAX_ITERATIONS = 25;
const ORTHOGONALIZE_CONVERGENCE_TOLERANCE = 1e-5; // metres (or project unit) of per-iteration movement
const ORTHOGONALIZE_STEP_FRACTION = 0.5;

function angleDegXY(A, C, B){
	const v1x = A.x - C.x, v1y = A.y - C.y;
	const v2x = B.x - C.x, v2y = B.y - C.y;
	const len1 = Math.hypot(v1x, v1y);
	const len2 = Math.hypot(v2x, v2y);
	if(len1 < 1e-12 || len2 < 1e-12) return 180; // degenerate (coincident points) - treat as straight, not eligible for either target meaningfully different from itself

	const dot = (v1x * v2x + v1y * v2y) / (len1 * len2);
	return (Math.acos(Math.max(-1, Math.min(1, dot))) * 180) / Math.PI;
}

// Exact, closed-form minimum-displacement point C' such that angle(A,C',B)
// equals targetAngleDeg, with A and B held fixed - see section 8's doc
// comment (this file, computeFixedAngleVertex) for the full derivation via
// the inscribed-angle-theorem circle locus. Shared by both orthogonalize's
// per-vertex step and computeFixedAngleVertex() itself.
export function projectPointToFixedAngleArc(A, C, B, targetAngleDeg){
	const L = dist2D(A, B);
	if(L < 1e-9) return clonePoint(C); // degenerate anchors, nothing meaningful to solve for

	const ex = (B.x - A.x) / L, ey = (B.y - A.y) / L;
	const nx = -ey, ny = ex;
	const Mx = (A.x + B.x) / 2, My = (A.y + B.y) / 2;

	const side = (nx * (C.x - Mx) + ny * (C.y - My)) >= 0 ? 1 : -1;

	const theta = (targetAngleDeg * Math.PI) / 180;
	const sinTheta = Math.sin(theta);
	if(Math.abs(sinTheta) < 1e-9){
		// target ~0°/180° with A,B distinct: the arc degenerates to the line
		// AB itself - the minimum-displacement point is C's own projection
		// onto line AB
		const t = ((C.x - A.x) * (B.x - A.x) + (C.y - A.y) * (B.y - A.y)) / (L * L);
		return {x: A.x + t * (B.x - A.x), y: A.y + t * (B.y - A.y), z: C.z};
	}

	const tanTheta = Math.tan(theta);
	let Ox, Oy, R;
	if(Math.abs(tanTheta) > 1e6){
		// target ~90° - Thales' theorem: the circle's centre is the chord's
		// own midpoint, radius = half the chord
		Ox = Mx; Oy = My; R = L / 2;
	}else{
		// k = (L/2)/tanTheta is positive for an acute target (<90°) and
		// negative for an obtuse one (>90°) - by the inscribed angle
		// theorem, the correct circle's centre sits on the SAME side as C
		// for an acute target and the OPPOSITE side for an obtuse one; the
		// single signed expression `side*k` gives exactly that in both
		// cases without a separate branch (verified against a hand-worked
		// example of each case - see vectorEditing.test.js).
		const k = (L / 2) / tanTheta;
		Ox = Mx + side * k * nx;
		Oy = My + side * k * ny;
		R = L / (2 * sinTheta);
	}

	const dx = C.x - Ox, dy = C.y - Oy;
	const distToCentre = Math.hypot(dx, dy);
	if(distToCentre < 1e-9){
		// C sits exactly on the centre (only possible if R≈0, i.e. A≈B,
		// already handled above) - arbitrary but deterministic fallback
		return {x: Ox + R, y: Oy, z: C.z};
	}

	const projected = {x: Ox + (R * dx) / distToCentre, y: Oy + (R * dy) / distToCentre, z: C.z};

	// The circle's chord AB splits the *full* circle into two arcs - one on
	// each side of line AB - but only the arc on C's own side actually
	// gives the target angle (the other arc gives its 180°-θ supplement).
	// A plain radial projection from the centre can still land on the wrong
	// arc when the circle is large enough to bulge across the chord line
	// (this happens for large/awkward angle changes, which fixedAngle's
	// user-facing "arbitrary custom angle" explicitly allows - orthogonalize
	// itself never triggers this branch in practice, since it only ever
	// touches vertices already within a small angleTolerance of the target).
	// When that happens, the true closest point *on the restricted arc* is
	// at whichever of the two arc endpoints (A or B - the fixed anchors
	// themselves) is nearer to C: a standard result for closest-point-on-
	// an-arc once the unrestricted closest point falls outside the valid
	// arc. In practice this is a large, degenerate correction that
	// computeFixedAngleVertex()'s own Maksimal pilhøjde check will reject
	// for any realistic pilhøjde budget - it is not silently "wrong", just
	// an honest answer to a request with no nearby solution.
	const projectedSide = (nx * (projected.x - Mx) + ny * (projected.y - My)) >= 0 ? 1 : -1;
	if(projectedSide !== side){
		const distA = dist2D(C, A), distB = dist2D(C, B);
		return distA <= distB ? clonePoint(A) : clonePoint(B);
	}

	return projected;
}

export function orthogonalizePolyline(points, angleToleranceDeg, maxSagitta, startIndex = 0, endIndex = points.length - 1){
	const original = points.map(clonePoint);
	const current = points.map(clonePoint);
	let maxDeviation = 0;

	for(let iter = 0; iter < ORTHOGONALIZE_MAX_ITERATIONS; iter++){
		const snapshot = current.map(clonePoint);
		let maxStep = 0;

		for(let i = startIndex + 1; i < endIndex; i++){
			const A = snapshot[i - 1], C = snapshot[i], B = snapshot[i + 1];
			const angle = angleDegXY(A, C, B);
			const target = Math.abs(angle - 90) <= Math.abs(angle - 180) ? 90 : 180;
			if(Math.abs(angle - target) > angleToleranceDeg) continue; // not close enough to either target - left untouched, per task requirement

			const exact = projectPointToFixedAngleArc(A, C, B, target);
			const stepped = {
				x: current[i].x + ORTHOGONALIZE_STEP_FRACTION * (exact.x - current[i].x),
				y: current[i].y + ORTHOGONALIZE_STEP_FRACTION * (exact.y - current[i].y),
			};

			// hard displacement budget, measured from the ORIGINAL position,
			// not this iteration's start - a vertex that has already used up
			// its budget over previous iterations simply stops moving
			const origP = original[i];
			const totalDx = stepped.x - origP.x, totalDy = stepped.y - origP.y;
			const totalDist = Math.hypot(totalDx, totalDy);
			let finalPos = stepped;
			if(totalDist > maxSagitta){
				const scale = maxSagitta / totalDist;
				finalPos = {x: origP.x + totalDx * scale, y: origP.y + totalDy * scale};
			}

			maxStep = Math.max(maxStep, dist2D(finalPos, current[i]));
			current[i] = {x: finalPos.x, y: finalPos.y, z: current[i].z};
			maxDeviation = Math.max(maxDeviation, dist2D(current[i], origP));
		}

		if(maxStep < ORTHOGONALIZE_CONVERGENCE_TOLERANCE) break;
	}

	return {points: current, maxDeviation};
}

// ── Polygon (closed ring) adapters for Forenkle/Ortogonaliser ───────────
//
// simplifyPolyline()/orthogonalizePolyline() above operate on a linear
// [startIndex..endIndex] index range and know nothing about a ring's own
// closing edge (the implicit segment from the last point back to the
// first - see FeatureVisual's own `closed`/`(i+1)%n` rendering convention).
// Reused here UNCHANGED (no new algorithm) via a "seam-duplicated ring"
// adapter: the ring is presented to the existing, already-tested function
// as an open (n+1)-point chain with points[0] repeated at the end, so the
// closing edge fully participates in the same tolerance/deviation math
// those functions already enforce - then the duplicate is dropped again on
// the way out. Both functions always keep their start/end anchor points
// completely unchanged (simplifyPolyline never discards them;
// orthogonalizePolyline's own loop runs only over `startIndex+1..endIndex-1`),
// so the trailing duplicate is always still there, identical to points[0],
// however many interior points either operation removed or moved.
//
// Known, deliberate limitation (not a validity concern, only a
// completeness one, and documented here rather than silently accepted):
// the seam vertex (points[0]) is pinned as the anchor at BOTH ends of the
// extended chain, so it can never itself be simplified away or re-angled -
// which of the ring's vertices happens to be "index 0" is an arbitrary
// artifact of digitization order, not a geometric property of the ring.
// A bespoke closed-ring-native algorithm that also handles the seam vertex
// itself was judged out of scope/over-engineering for this iteration -
// every OTHER vertex is fully eligible, and the caller always re-validates
// the result afterwards (see geometry/polygonValidity.js's
// isValidPolygonRing()) before committing it.
export function simplifyClosedRing(points, tolerance){
	const extended = [...points, clonePoint(points[0])];
	const result = simplifyPolyline(extended, tolerance, 0, extended.length - 1);
	return {points: result.points.slice(0, -1), maxDeviation: result.maxDeviation};
}

export function orthogonalizeClosedRing(points, angleToleranceDeg, maxSagitta){
	const extended = [...points, clonePoint(points[0])];
	const result = orthogonalizePolyline(extended, angleToleranceDeg, maxSagitta, 0, extended.length - 1);
	return {points: result.points.slice(0, -1), maxDeviation: result.maxDeviation};
}

// ── section 8: Fast vinkel ────────────────────────────────────────────────
//
// Scope of this first implementation: exactly the workflow the task itself
// describes - the user selects two connected segments sharing one common
// vertex (three points A, C, B, C the shared one). A and B - the outer
// selection boundary - are the "endpoints [that] necessarily function as
// boundary anchors" the task allows keeping fixed; C is the only free
// vertex, and projectPointToFixedAngleArc() gives the EXACT minimum-
// displacement position for it (not an iterative approximation - with only
// one free point and two fixed anchors, the closed-form circle-locus
// solution IS the global optimum, so there is nothing to gain from
// iterating). See that function's doc comment for the derivation.
//
// Returns null if the required angle cannot be met without exceeding
// maxSagitta - the task requires the operation be refused (or require
// explicit confirmation) in that case rather than silently exceeding the
// limit; the caller decides which (see tools/vectorEditingMenu.js).
export function computeFixedAngleVertex(A, C, B, targetAngleDeg, maxSagitta){
	const solved = projectPointToFixedAngleArc(A, C, B, targetAngleDeg);
	const deviation = dist2D(solved, C);

	if(deviation > maxSagitta) return null;

	return {point: solved, deviation};
}

// ── section 9: Parallel / vinkelret på reference ─────────────────────────
//
// Anchor rule (task requirement: "dokumentér anchor-reglen"): the segment
// is rotated about its OWN midpoint, both endpoints moving by equal and
// opposite amounts. This is the exact minimum-displacement solution for
// "rotate this segment to a new direction while preserving its length" (a
// rigid rotation about any other pivot would move at least one endpoint
// farther for the same length-preserving result) - and it's symmetric, so
// neither endpoint is arbitrarily privileged.
//
// This does NOT open a gap to neighbouring segments, including when BOTH
// of the target segment's endpoints are shared with a neighbour (an
// "internal" segment). computeSegmentAlignment()/applyAlignedSegment()
// below always operate on the feature's FULL vertex array (the caller
// passes every vertex, not just the two being realigned - see
// VectorEditingMenu.js's toPlainPoints()) and write the two new endpoints
// back into a clone of that SAME array at their original indices; every
// other vertex, including the ones a neighbouring segment reads from, is
// untouched. Since a neighbouring segment reads its shared endpoint from
// that exact array index (see FeatureVisual._layoutEdges()), it always
// sees the realigned position - there is no separate/copied segment
// representation that could drift apart. Verified empirically in
// vectorEditing.test.js ("neighbour connectivity (gap investigation)"),
// covering the first, an internal, and the last segment of a 4-vertex
// polyline, for both parallel and perpendicular.
//
// `refDirXY` is the reference direction, {x,y} (need not be unit length).
// The realigned segment is pointed in whichever of ±refDir keeps it closest
// to its own original direction (never an arbitrary 180° flip). Z on both
// endpoints is preserved unchanged (XY-only geometry, per task section 14).
function alignSegmentToDirection(A, B, dirX, dirY){
	const dirLen = Math.hypot(dirX, dirY);
	if(dirLen < 1e-12) return null;
	const ux = dirX / dirLen, uy = dirY / dirLen;

	const length = dist2D(A, B);
	const Mx = (A.x + B.x) / 2, My = (A.y + B.y) / 2;

	const origDx = B.x - A.x, origDy = B.y - A.y;
	const sign = (origDx * ux + origDy * uy) >= 0 ? 1 : -1;

	const halfX = (sign * ux * length) / 2, halfY = (sign * uy * length) / 2;
	const newA = {x: Mx - halfX, y: My - halfY, z: A.z};
	const newB = {x: Mx + halfX, y: My + halfY, z: B.z};

	const maxDeviation = Math.max(dist2D(newA, A), dist2D(newB, B));
	return {A: newA, B: newB, maxDeviation};
}

// Ungated diagnostic variant, shared by alignSegmentParallel/Perpendicular
// below - always returns the computed result and its maxDeviation, even
// when that exceeds a caller's pilhøjde budget, so a UI layer can show the
// actual figure in a refusal dialog (see alignSegmentParallel/Perpendicular
// themselves for the gated, task-facing versions - this is what they call).
export function computeSegmentAlignment(points, segmentIndex, refDirXY, mode){
	const A = points[segmentIndex], B = points[segmentIndex + 1];
	const dir = mode === "perpendicular" ? {x: -refDirXY.y, y: refDirXY.x} : refDirXY;
	return alignSegmentToDirection(A, B, dir.x, dir.y);
}

function applyAlignedSegment(points, segmentIndex, aligned, maxSagitta){
	if(!aligned || aligned.maxDeviation > maxSagitta) return null;

	const out = points.map(clonePoint);
	out[segmentIndex] = aligned.A;
	out[segmentIndex + 1] = aligned.B;
	return {points: out, maxDeviation: aligned.maxDeviation};
}

export function alignSegmentParallel(points, segmentIndex, refDirXY, maxSagitta){
	return applyAlignedSegment(points, segmentIndex, computeSegmentAlignment(points, segmentIndex, refDirXY, "parallel"), maxSagitta);
}

export function alignSegmentPerpendicular(points, segmentIndex, refDirXY, maxSagitta){
	return applyAlignedSegment(points, segmentIndex, computeSegmentAlignment(points, segmentIndex, refDirXY, "perpendicular"), maxSagitta);
}

// ── section 10: Indsæt punkt ──────────────────────────────────────────────
//
// New vertex at parameter t∈[0,1] along segment [segmentIndex,segmentIndex+1]
// (linear interpolation, full XYZ - "korrekt XYZ-position" per task), which
// splits that segment into two; every other vertex is untouched.
export function insertPointOnSegment(points, segmentIndex, t){
	const A = points[segmentIndex], B = points[segmentIndex + 1];
	const clampedT = Math.max(0, Math.min(1, t));
	const newPoint = {
		x: A.x + clampedT * (B.x - A.x),
		y: A.y + clampedT * (B.y - A.y),
		z: A.z + clampedT * (B.z - A.z),
	};

	const out = points.slice(0, segmentIndex + 1).map(clonePoint);
	out.push(newPoint);
	out.push(...points.slice(segmentIndex + 1).map(clonePoint));
	return out;
}

// ── section 11: Vend retning ──────────────────────────────────────────────
//
// Pure vertex-order reversal - the geometry in space is identical, only the
// P0..Pn traversal direction flips. No coordinate is altered.
export function reverseDirection(points){
	return points.map(clonePoint).reverse();
}

// ── section 12: Flyt punkt ───────────────────────────────────────────────
//
// Replaces a single vertex's position outright - not a constraint-solve
// like the operations above, so there is nothing to project/rotate/clamp:
// the user is deliberately placing this vertex wherever they pick in the
// point cloud. Every other vertex (including this one's neighbours) is
// copied through unchanged, which is what keeps the feature's connectivity
// intact - the moved vertex is still the exact same shared array entry its
// neighbouring segments read from (same principle as section 9's anchor
// rule - see that section's doc comment).
//
// `maxDeviation` is still reported (distance actually moved, full 3D) for
// consistency with every other operation's return shape, but callers do
// NOT gate this on "Maksimal pilhøjde" - that setting bounds how far an
// AUTOMATED geometric correction is allowed to drift a vertex from its
// original position; Flyt punkt has no "original correct position" to
// drift from; it's the user directly choosing the new one.
export function movePoint(points, index, newPosition){
	const out = points.map(clonePoint);
	const original = points[index];
	out[index] = clonePoint(newPosition);
	return {points: out, maxDeviation: dist3D(original, out[index])};
}

// ── section 13: Fordel punkter lige ──────────────────────────────────────
//
// points[startIndex] and points[endIndex] are kept fixed as anchors (same
// convention as every range operation above); every strictly-interior
// vertex is repositioned to sit at k/(count) of the TOTAL path length
// walked along the CURRENT segments from startIndex to endIndex - full 3D
// linear interpolation within whichever segment that fraction falls in, not
// a straight chord between the anchors (that's Ret ud, a separate
// operation) - so the line's overall route/shape is preserved, only the
// spacing of its interior vertices changes. The number of vertices never
// changes, only their positions.
//
// Not gated by maxSagitta: like reverseDirection()/movePoint(), this is
// exactly what the user explicitly asked for, not an automated correction
// with a "how far is too far" tolerance - maxDeviation is still reported
// for consistency with every other operation's return shape.
export function distributePointsEvenly(points, startIndex = 0, endIndex = points.length - 1){
	const result = points.map(clonePoint);
	const count = endIndex - startIndex; // segments spanned, and also the number of equal shares
	if(count < 2) return {points: result, maxDeviation: 0}; // no interior vertex to move

	const segmentLengths = [];
	let total = 0;
	for(let i = startIndex; i < endIndex; i++){
		const len = dist3D(points[i], points[i + 1]);
		segmentLengths.push(len);
		total += len;
	}

	if(total < 1e-12) return {points: result, maxDeviation: 0}; // degenerate (coincident) path - nothing meaningful to distribute along

	let maxDeviation = 0;

	for(let k = 1; k < count; k++){
		const target = (total * k) / count;

		let accumulated = 0;
		let segIndex = 0;
		while(segIndex < segmentLengths.length - 1 && accumulated + segmentLengths[segIndex] < target){
			accumulated += segmentLengths[segIndex];
			segIndex++;
		}

		const segLen = segmentLengths[segIndex];
		const t = segLen > 1e-12 ? (target - accumulated) / segLen : 0;
		const A = points[startIndex + segIndex], B = points[startIndex + segIndex + 1];
		const newPos = {
			x: A.x + t * (B.x - A.x),
			y: A.y + t * (B.y - A.y),
			z: A.z + t * (B.z - A.z),
		};

		maxDeviation = Math.max(maxDeviation, dist3D(points[startIndex + k], newPos));
		result[startIndex + k] = newPos;
	}

	return {points: result, maxDeviation};
}

// ── section 14: Flyt punkt i Z ────────────────────────────────────────────
//
// Moves exactly one vertex straight up/down: X and Y are left completely
// untouched, only Z changes by `deltaZ` (positive = up, negative = down).
// Applies to ANY vertex - a standalone Point feature, an interior line
// vertex, or an endpoint - there is no role-based restriction, same as
// movePoint() itself. A thin, dedicated wrapper around movePoint() (so the
// two can never drift apart on how a vertex is replaced or how
// maxDeviation is reported) rather than a hand-rolled copy, but still its
// own exported function/name - "Flyt punkt i Z" is a distinct user-facing
// menu action (its own dialog: a signed cm value, see VectorEditingMenu.js's
// promptSignedDistanceCm()) and deserves its own entry here, exactly like
// every other operation in this file.
//
// Not gated by Maksimal pilhøjde, same reasoning as movePoint(): this is an
// exact, user-chosen displacement, not an automated geometric correction
// with a "how far is too far" budget.
export function movePointInZ(points, index, deltaZ){
	const p = points[index];
	return movePoint(points, index, {x: p.x, y: p.y, z: p.z + deltaZ});
}

// ── Flyt linje/segment/del i Z ────────────────────────────────────────────
//
// The multi-vertex sibling of movePointInZ(): moves vertices startIndex..endIndex (inclusive) straight up/down by the SAME `deltaZ`;
// X and Y of every vertex, and all vertices outside the span, are untouched. The span is the whole line (0..last) for "Flyt linje i Z",
// the two endpoints of one segment for "Flyt segment i Z", and the range's vertices for "Flyt del i Z". A rigid shift, so the shape of the
// moved part is unchanged. Not gated by Maksimal pilhøjde (an exact, user-chosen displacement, same reasoning as movePointInZ()).
// maxDeviation = |deltaZ| (every moved vertex moves by exactly that).
export function movePointsInZ(points, startIndex, endIndex, deltaZ){
	const out = points.map(clonePoint);
	const from = Math.max(0, Math.min(startIndex, endIndex));
	const to = Math.min(points.length - 1, Math.max(startIndex, endIndex));
	for(let i = from; i <= to; i++) out[i] = {...out[i], z: out[i].z + deltaZ};
	return {points: out, maxDeviation: Math.abs(deltaZ)};
}

// ── section 16: Sæt kote (absolut Z) ──────────────────────────────────────
//
// Sets exactly one vertex's Z to an ABSOLUTE value the user types - X and Y
// are left completely untouched. Same "any vertex, no role restriction" rule
// and thin-wrapper-around-movePoint() construction as movePointInZ() above;
// the only difference is the input is an absolute kote (`z' = z_input`), not
// a relative Δz. Not gated by Maksimal pilhøjde, same reasoning as
// movePointInZ()/movePoint().
export function setAbsoluteZ(points, index, zValue){
	const p = points[index];
	return movePoint(points, index, {x: p.x, y: p.y, z: zValue});
}

// ── section 15: Forlæng endesegment (fast afstand i egen retning) ────────
//
// Moves the chosen endpoint (index 0 or the last index) along the direction
// of its OWN terminal segment - away from its neighbour for a positive
// `distanceMeters` (lengthens the line, "frem"), or back toward it for a
// negative one (shortens the line, "tilbage"). Full 3D direction: an end
// segment that runs downhill keeps running downhill for the extension,
// exactly like extendPolylineToReference() in geometry/lineSplit.js already
// does for its own Z - but UNLIKE that function, this one never looks at
// any other feature's geometry at all; "how far" is simply the user's own
// number, not an intersection with a reference line.
//
// Refuses ({error}) when:
//   - the line has fewer than 2 points, or `endpointIndex` is not an
//     endpoint (0 or length-1) - "too-few-points" / "not-an-endpoint", same
//     codes lineSplit.js's own endpoint operations already use;
//   - the terminal segment is degenerate (the endpoint already coincides
//     with its neighbour): there is no direction to extend along -
//     "degenerate-segment";
//   - a negative `distanceMeters` would move the endpoint TO OR PAST its
//     own neighbour, collapsing or reversing the terminal segment -
//     "would-collapse-segment". Shrinking the segment down to any strictly
//     positive length is fine; crossing past its own other end is not a
//     shorter segment, it is a different, unintended line.
//
// Not gated by Maksimal pilhøjde - like movePoint()/distributePointsEvenly()
// above, this is exactly the fixed, user-chosen distance the user asked
// for, not an automated correction with a deviation budget.
export function extendEndpointByDistance(points, endpointIndex, distanceMeters){
	if(points.length < 2) return {error: "too-few-points"};
	const isEnd = endpointIndex === points.length - 1;
	if(endpointIndex !== 0 && !isEnd) return {error: "not-an-endpoint"};

	const endpoint = points[endpointIndex];
	const neighbour = points[isEnd ? endpointIndex - 1 : endpointIndex + 1];
	const segLen = dist3D(endpoint, neighbour);
	if(segLen < 1e-9) return {error: "degenerate-segment"};

	if(distanceMeters < 0 && -distanceMeters >= segLen) return {error: "would-collapse-segment"};

	const ux = (endpoint.x - neighbour.x) / segLen;
	const uy = (endpoint.y - neighbour.y) / segLen;
	const uz = (endpoint.z - neighbour.z) / segLen;

	const result = points.map(clonePoint);
	result[endpointIndex] = {
		x: endpoint.x + ux * distanceMeters,
		y: endpoint.y + uy * distanceMeters,
		z: endpoint.z + uz * distanceMeters,
	};
	return {points: result, maxDeviation: Math.abs(distanceMeters)};
}

export {dist2D, dist3D, angleDegXY};
